import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Brain, Globe, Megaphone, Cpu, GraduationCap, Battery, CreditCard, Sparkles, ArrowUpRight,
} from 'lucide-react';
import Reveal from '../Reveal';
import { SectionHeading } from '../shared';

// Ecosystem nodes: center + 6 orbital nodes forming a connected graph.
// Each orbital node has angle (deg) and short label; clicking opens its page.
const CENTER = {
  id: 'dts',
  label: 'DTS',
  sub: 'Digital Hub',
  icon: Brain,
  color: '#c6f24e', // lime
};

const NODES = [
  {
    id: 'web',
    angle: -90,          // top
    label: 'Web & Marketing',
    sub: 'Siti · Ads · Funnel',
    icon: Globe,
    color: '#67e8f9',   // cyan
    href: '/servizi/siti-web',
    related: ['ads', 'ai'],
  },
  {
    id: 'ads',
    angle: -30,          // top-right
    label: 'Campagne ADS',
    sub: 'Visibilita · Lead',
    icon: Megaphone,
    color: '#f0abfc',   // fuchsia
    href: '/servizi/campagne-ads',
    related: ['web', 'ai'],
  },
  {
    id: 'ai',
    angle: 30,           // bottom-right
    label: 'AI & Automazione',
    sub: 'AI Act · Advisor',
    icon: Cpu,
    color: '#c6f24e',   // lime
    href: '/consulenza-ai-act',
    related: ['web', 'ads', 'formazione'],
  },
  {
    id: 'formazione',
    angle: 90,           // bottom
    label: 'Formazione ACDT',
    sub: 'Certificazioni · Tutor AI',
    icon: GraduationCap,
    color: '#fca5a5',   // red-ish rose
    href: '/formazione',
    related: ['ai', 'pos'],
  },
  {
    id: 'pos',
    angle: 150,          // bottom-left
    label: 'POS & Pagamenti',
    sub: 'SpazioPOS · B100',
    icon: CreditCard,
    color: '#fcd34d',   // amber
    href: '/servizi/pos-spaziopos',
    related: ['formazione', 'chargebull'],
  },
  {
    id: 'chargebull',
    angle: 210,          // top-left
    label: 'ChargeBull',
    sub: 'Totem gratis · Pubblicita',
    icon: Battery,
    color: '#86efac',   // emerald
    href: '/servizi/chargebull-free',
    related: ['pos', 'web'],
  },
];

const polarToXY = (angleDeg, radius, cx = 50, cy = 50) => {
  const r = (angleDeg * Math.PI) / 180;
  return { x: cx + Math.cos(r) * radius, y: cy + Math.sin(r) * radius };
};

export default function EcosystemGraph() {
  const [active, setActive] = useState(null); // node id hovered/focused
  const [mounted, setMounted] = useState(false);
  const svgRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 150);
    return () => clearTimeout(t);
  }, []);

  const RADIUS = 36; // % of viewBox (100x100)

  const nodesXY = NODES.map((n) => ({ ...n, ...polarToXY(n.angle, RADIUS) }));

  // Build edges: center<->node + node<->related
  const centerEdges = nodesXY.map((n) => ({ id: `c-${n.id}`, from: { x: 50, y: 50 }, to: { x: n.x, y: n.y }, color: n.color, nodeId: n.id }));
  const peerEdges = [];
  const seen = new Set();
  nodesXY.forEach((n) => {
    (n.related || []).forEach((rid) => {
      const key = [n.id, rid].sort().join('-');
      if (seen.has(key)) return;
      seen.add(key);
      const target = nodesXY.find((x) => x.id === rid);
      if (!target) return;
      peerEdges.push({ id: `p-${key}`, from: { x: n.x, y: n.y }, to: { x: target.x, y: target.y }, color: n.color, pair: [n.id, rid] });
    });
  });

  const isEdgeActive = (edge) => {
    if (!active) return false;
    if (edge.nodeId) return edge.nodeId === active;
    if (edge.pair) return edge.pair.includes(active);
    return false;
  };

  return (
    <section className="relative py-20 md:py-28 overflow-hidden" data-testid="home-ecosystem" data-advisor-target="servizi">
      {/* Decorative background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-lime/5 blur-[120px]" />
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-8 relative">
        <Reveal>
          <SectionHeading
            label="ecosistema dts"
            title="Un ecosistema digitale"
            highlight="che si potenzia da solo"
          />
          <p className="text-white/55 text-lg mt-6 max-w-2xl">
            Ogni servizio e un nodo connesso a tutti gli altri. Vendite, formazione, pagamenti, intelligenza artificiale e visibilita si rinforzano a vicenda per generare risultati reali.
          </p>
        </Reveal>

        <div className="mt-14 grid lg:grid-cols-[1fr_380px] gap-8 items-center">
          {/* GRAPH */}
          <Reveal>
            <div className="relative aspect-square max-w-[640px] mx-auto">
              <svg
                ref={svgRef}
                viewBox="0 0 100 100"
                className="w-full h-full"
                data-testid="ecosystem-svg"
                aria-label="Grafo ecosistema DTS"
                style={{ overflow: 'visible' }}
              >
                <defs>
                  <radialGradient id="centerGlow" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="#c6f24e" stopOpacity="0.6" />
                    <stop offset="100%" stopColor="#c6f24e" stopOpacity="0" />
                  </radialGradient>
                  {NODES.map((n) => (
                    <linearGradient key={n.id} id={`grad-${n.id}`} x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor={n.color} stopOpacity="0.9" />
                      <stop offset="100%" stopColor="#c6f24e" stopOpacity="0.6" />
                    </linearGradient>
                  ))}
                </defs>

                {/* Center glow */}
                <circle cx="50" cy="50" r="20" fill="url(#centerGlow)" />

                {/* Orbit rings */}
                <circle cx="50" cy="50" r={RADIUS} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="0.2" strokeDasharray="0.6 0.8" />
                <circle cx="50" cy="50" r={RADIUS - 8} fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth="0.15" />

                {/* Peer edges (background) */}
                {peerEdges.map((e) => {
                  const activeE = isEdgeActive(e);
                  return (
                    <line
                      key={e.id}
                      x1={e.from.x} y1={e.from.y}
                      x2={e.to.x} y2={e.to.y}
                      stroke={activeE ? e.color : 'rgba(255,255,255,0.08)'}
                      strokeWidth={activeE ? 0.4 : 0.2}
                      strokeDasharray="0.8 0.6"
                      className="transition-all duration-300"
                    />
                  );
                })}

                {/* Center-to-node edges with animated flow */}
                {centerEdges.map((e, i) => {
                  const activeE = isEdgeActive(e);
                  return (
                    <g key={e.id}>
                      <line
                        x1={e.from.x} y1={e.from.y}
                        x2={e.to.x} y2={e.to.y}
                        stroke={activeE ? e.color : `${e.color}55`}
                        strokeWidth={activeE ? 0.5 : 0.3}
                        className="transition-all duration-300"
                      />
                      {/* Flowing pulse along edge */}
                      <circle r="0.7" fill={e.color} opacity={mounted ? 0.9 : 0}>
                        <animateMotion
                          dur={`${3 + (i % 3)}s`}
                          repeatCount="indefinite"
                          begin={`${i * 0.4}s`}
                          path={`M ${e.from.x} ${e.from.y} L ${e.to.x} ${e.to.y}`}
                        />
                      </circle>
                    </g>
                  );
                })}

                {/* Center node */}
                <g className="cursor-pointer" onMouseEnter={() => setActive('dts')} onMouseLeave={() => setActive(null)}>
                  <circle cx="50" cy="50" r="9" fill="#0a1230" stroke="#c6f24e" strokeWidth="0.6" className="ecosystem-pulse" />
                  <foreignObject x="43" y="43" width="14" height="14" style={{ overflow: 'visible' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
                      <Brain style={{ width: '8px', height: '8px', color: '#c6f24e' }} />
                    </div>
                  </foreignObject>
                </g>

                {/* Orbital nodes */}
                {nodesXY.map((n) => {
                  const isActive = active === n.id;
                  return (
                    <g
                      key={n.id}
                      className="cursor-pointer"
                      onMouseEnter={() => setActive(n.id)}
                      onMouseLeave={() => setActive(null)}
                      onFocus={() => setActive(n.id)}
                      onBlur={() => setActive(null)}
                      onClick={() => navigate(n.href)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(n.href); }
                      }}
                      data-testid={`ecosystem-node-${n.id}`}
                      role="button"
                      tabIndex={0}
                      aria-label={`Vai a ${n.label}`}
                    >
                      <circle cx={n.x} cy={n.y} r={isActive ? 7 : 6} fill="#0a1230" stroke={n.color} strokeWidth={isActive ? 0.7 : 0.45} className="transition-all duration-300" />
                      {isActive && (
                        <circle cx={n.x} cy={n.y} r="8.5" fill="none" stroke={n.color} strokeWidth="0.3" opacity="0.5">
                          <animate attributeName="r" from="7" to="11" dur="1.4s" repeatCount="indefinite" />
                          <animate attributeName="opacity" from="0.6" to="0" dur="1.4s" repeatCount="indefinite" />
                        </circle>
                      )}
                      <foreignObject x={n.x - 3} y={n.y - 3} width="6" height="6" style={{ overflow: 'visible', pointerEvents: 'none' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
                          <n.icon style={{ width: '4px', height: '4px', color: n.color }} />
                        </div>
                      </foreignObject>
                    </g>
                  );
                })}
              </svg>
            </div>
          </Reveal>

          {/* NODE LEGEND / INFO PANEL */}
          <Reveal delay={200}>
            <div className="space-y-3" data-testid="ecosystem-legend">
              <div className="rounded-2xl border border-lime/30 bg-lime/5 p-4 flex items-center gap-3">
                <span className="w-10 h-10 rounded-xl bg-lime/15 flex items-center justify-center shrink-0">
                  <Sparkles size={18} className="text-lime" />
                </span>
                <div>
                  <p className="font-display font-bold text-white text-sm">Passa il mouse sui nodi</p>
                  <p className="text-white/55 text-xs">Scopri le connessioni tra i servizi.</p>
                </div>
              </div>
              {NODES.map((n) => {
                const isActive = active === n.id;
                return (
                  <Link
                    key={n.id}
                    to={n.href}
                    onMouseEnter={() => setActive(n.id)}
                    onMouseLeave={() => setActive(null)}
                    onFocus={() => setActive(n.id)}
                    onBlur={() => setActive(null)}
                    data-testid={`ecosystem-link-${n.id}`}
                    className={`group flex items-center gap-3 rounded-xl border p-3 transition-all ${
                      isActive
                        ? 'border-white/30 bg-white/[0.06] translate-x-1'
                        : 'border-white/10 bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.04]'
                    }`}
                    style={isActive ? { borderColor: `${n.color}70`, boxShadow: `0 0 20px -5px ${n.color}40` } : {}}
                  >
                    <span
                      className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-all"
                      style={{ background: isActive ? `${n.color}25` : `${n.color}15` }}
                    >
                      <n.icon size={18} style={{ color: n.color }} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-display font-bold text-white text-sm leading-tight truncate">{n.label}</p>
                      <p className="text-white/50 text-xs truncate">{n.sub}</p>
                    </div>
                    <ArrowUpRight size={14} className="text-white/40 group-hover:text-white group-hover:rotate-45 transition-all shrink-0" />
                  </Link>
                );
              })}
            </div>
          </Reveal>
        </div>
      </div>

      <style>{`
        .ecosystem-pulse {
          animation: ecoPulse 2.6s ease-in-out infinite;
        }
        @keyframes ecoPulse {
          0%, 100% { stroke-width: 0.6; filter: drop-shadow(0 0 2px #c6f24e); }
          50% { stroke-width: 1; filter: drop-shadow(0 0 6px #c6f24e); }
        }
      `}</style>
    </section>
  );
}
